import { useCallback, useEffect, useState } from 'react';
import storeLocationsService from '../services/storeLocationsService';
import type { StoreLocationRegister } from '../../../../types/features/storeDetails/locations';

export const useStoreLocationRegisters = (storeId: string) => {
  const [registers, setRegisters] = useState<StoreLocationRegister[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingRegisterId, setUpdatingRegisterId] = useState('');

  const loadRegisters = useCallback(async () => {
    if (!storeId) {
      setRegisters([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await storeLocationsService.getStoreLocationRegisters(storeId);
      setRegisters(response);
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : 'Failed to load register mappings.';
      setError(message);
      setRegisters([]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  const handleToggleActive = useCallback(async (register: StoreLocationRegister, nextActive: boolean) => {
    if (!storeId) {
      return;
    }

    setUpdatingRegisterId(register.id);
    setError('');

    try {
      await storeLocationsService.updateStoreLocationRegister(storeId, register.id, {
        active: nextActive,
      });
      await loadRegisters();
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : 'Failed to update register mapping.';
      setError(message);
    } finally {
      setUpdatingRegisterId('');
    }
  }, [loadRegisters, storeId]);

  useEffect(() => {
    void loadRegisters();
  }, [loadRegisters]);

  return {
    error,
    loading,
    loadRegisters,
    registers,
    handleToggleActive,
    updatingRegisterId,
  };
};
